// src/components/ChatPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sparkles, Send, Moon, ChevronDown, Plus, Trash2, Sun, User, Flame,
  MessageCircle, Heart, Gem, RefreshCw, Image, Eye, Zap
} from "lucide-react";

import { DISABLE_AUTH } from "../config/flags";
import { useAuth } from "../hooks/useAuth";
import { useConversations } from "../hooks/useConversations";
import { useSoulFile } from "../hooks/useSoulFile";

// Import tool components
import { OracleCardPull } from "./oracle/OracleCardPull";
import { AffirmationGenerator } from "./affirmations/AffirmationGenerator";
import { GratitudeJournal } from "./gratitude/GratitudeJournal";
import { EvidenceBank } from "./evidence/EvidenceBank";
import { MicroChallenges } from "./challenges/MicroChallenges";
import { VisionBoard } from "./vision-board/VisionBoard";
import { GuidedVisualization } from "./visualizations/GuidedVisualization";
import { DailyAlignment } from "./rituals/DailyAlignment";
import { WeeklyRitual } from "./rituals/WeeklyRitual";
import { SoulFileDashboard } from "./soul-file/SoulFileDashboard";

// Import new unified header components
import { UnifiedHeader } from "./UnifiedHeader";
import { SettingsDropdown } from "./SettingsDropdown";

type ActiveView =
  | 'chat'
  | 'soul-file'
  | 'oracle'
  | 'affirmations'
  | 'gratitude'
  | 'evidence'
  | 'aligned-actions'
  | 'vision-board'
  | 'visualizations'
  | 'ritual-morning'
  | 'ritual-evening'
  | 'ritual-reset';

const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: isLoadingAuth } = useAuth();
  const { soulFile } = useSoulFile();
  const {
    currentConversation, messages, conversations, loading: conversationsLoading,
    startConversation, loadConversation, sendMessage, addMystaraMessage, deleteConversation,
  } = useConversations();

  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatsExpanded, setChatsExpanded] = useState(true);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [activeView, setActiveView] = useState<ActiveView>('chat');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => { scrollToBottom(); }, [messages]);

  // Handle URL params for direct access
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view') as ActiveView;
    const validViews: ActiveView[] = ['soul-file', 'oracle', 'affirmations', 'gratitude', 'evidence', 'aligned-actions', 'vision-board', 'visualizations', 'ritual-morning', 'ritual-evening', 'ritual-reset'];
    if (view && validViews.includes(view)) {
      setActiveView(view);
    }
  }, [location.search]);

  // Tab change handler for unified header
  const handleTabChange = (tab: 'chat' | 'soul-file') => {
    setActiveView(tab);
  };

  const buildContext = () => {
    if (!soulFile) return null;
    return {
      userName: soulFile.essence.displayName,
      preferredName: soulFile.essence.preferredName || soulFile.essence.displayName,
      communicationStyle: soulFile.essence.communicationStyle,
      currentPhase: soulFile.journey.currentPhase,
      currentStreak: soulFile.stats.currentStreak,
      activeManifestations: soulFile.manifestations.filter(m => m.status === 'active').slice(0, 3).map(m => ({
        title: m.title, feeling: m.feelingUnderneath, identityStatement: m.identityStatement
      })),
      coreValues: soulFile.essence.coreValues,
      gifts: soulFile.essence.gifts,
      observedPatterns: soulFile.mystaraNotes.observedPatterns,
    };
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    let conversationId = currentConversation?.id;
    if (!conversationId) {
      try { conversationId = await startConversation('open'); } 
      catch (err) { console.error(err); return; }
    }
    const userMessage = await sendMessage(inputMessage);
    if (!userMessage) return;
    setInputMessage("");
    setIsLoading(true);

    try {
      const apiMessages = [...messages, userMessage].map((m) => ({
        role: m.role === 'user' ? 'user' : 'assistant', content: m.content,
      }));
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: apiMessages, context: buildContext(), sessionType: 'open' }),
      });
      const data = await res.json();
      await addMystaraMessage(data?.reply ?? "✨ I'm here with you.", data?.voiceUsed);
    } catch (err) {
      await addMystaraMessage("✨ I felt a little cosmic hiccup. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const handleNewChat = async () => { try { await startConversation('open'); } catch (err) { console.error(err); } };
  const handleLoadChat = async (id: string) => { try { await loadConversation(id); } catch (err) { console.error(err); } };
  const handleDeleteChat = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Delete this conversation?')) await deleteConversation(id);
  };

  const filteredConversations = conversations.filter((c) => c.title.toLowerCase().includes(chatSearchQuery.toLowerCase()));

  if (!DISABLE_AUTH && (isLoadingAuth || !user)) {
    return (
      <div className="mystara-container">
        <div className="mystara-orbs" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="mystara-glass p-8 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-600 animate-pulse" />
            <p className="text-purple-700">Loading your sanctuary...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nosedive chat-scope">
      <div className="mystara-orbs" />

      {/* Unified Header */}
      <UnifiedHeader
        activeTab={activeView === 'soul-file' ? 'soul-file' : 'chat'}
        onTabChange={handleTabChange}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      {/* Settings Dropdown */}
      <SettingsDropdown
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      <div className="mystara-content">
        <aside className="mystara-sidebar">
          <div className="mystara-glass p-4 mb-4">
            <button onClick={handleNewChat} className="mystara-btn w-full justify-center">
              <Plus className="w-4 h-4" /><span>New Conversation</span>
            </button>
          </div>

          <div className="mystara-glass p-4 mb-4">
            <h3 className="text-sm font-medium text-purple-700 mb-3 flex items-center gap-2">
              <span>✨</span>
              Daily Alignment
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setActiveView('ritual-morning')}
                className="group p-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/50 hover:border-amber-300 transition-all text-center"
              >
                <div className="text-2xl mb-1">☀️</div>
                <span className="text-xs font-medium text-amber-700 group-hover:text-amber-800">Morning</span>
              </button>
              <button
                onClick={() => setActiveView('ritual-evening')}
                className="group p-3 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200/50 hover:border-indigo-300 transition-all text-center"
              >
                <div className="text-2xl mb-1">🌙</div>
                <span className="text-xs font-medium text-indigo-700 group-hover:text-indigo-800">Evening</span>
              </button>
            </div>
          </div>

          <div className="mystara-glass p-4 mb-4">
            <p className="text-sm font-semibold text-purple-700 mb-3">🔮 Tools</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setActiveView('oracle')} className={`mystara-card p-2 flex flex-col items-center gap-1 ${activeView === 'oracle' ? 'ring-2 ring-purple-400 bg-purple-50' : 'hover:bg-purple-50/50'}`}>
                <Moon className="w-4 h-4 text-purple-500" /><span className="text-[10px] text-purple-600">Oracle</span>
              </button>
              <button onClick={() => setActiveView('affirmations')} className={`mystara-card p-2 flex flex-col items-center gap-1 ${activeView === 'affirmations' ? 'ring-2 ring-pink-400 bg-pink-50' : 'hover:bg-pink-50/50'}`}>
                <Sparkles className="w-4 h-4 text-pink-500" /><span className="text-[10px] text-purple-600">Affirm</span>
              </button>
              <button onClick={() => setActiveView('gratitude')} className={`mystara-card p-2 flex flex-col items-center gap-1 ${activeView === 'gratitude' ? 'ring-2 ring-rose-400 bg-rose-50' : 'hover:bg-rose-50/50'}`}>
                <Heart className="w-4 h-4 text-rose-500" /><span className="text-[10px] text-purple-600">Gratitude</span>
              </button>
              <button onClick={() => setActiveView('evidence')} className={`mystara-card p-2 flex flex-col items-center gap-1 ${activeView === 'evidence' ? 'ring-2 ring-cyan-400 bg-cyan-50' : 'hover:bg-cyan-50/50'}`}>
                <Gem className="w-4 h-4 text-cyan-500" /><span className="text-[10px] text-purple-600">Evidence</span>
              </button>
              <button onClick={() => setActiveView('ritual-reset')} className={`mystara-card p-2 flex flex-col items-center gap-1 ${activeView === 'ritual-reset' ? 'ring-2 ring-violet-400 bg-violet-50' : 'hover:bg-violet-50/50'}`}>
                <RefreshCw className="w-4 h-4 text-violet-500" /><span className="text-[10px] text-purple-600">Weekly Reset</span>
              </button>
              <button onClick={() => setActiveView('vision-board')} className={`mystara-card p-2 flex flex-col items-center gap-1 ${activeView === 'vision-board' ? 'ring-2 ring-pink-400 bg-pink-50' : 'hover:bg-pink-50/50'}`}>
                <Image className="w-4 h-4 text-pink-500" /><span className="text-[10px] text-purple-600">Vision</span>
              </button>
              <button onClick={() => setActiveView('visualizations')} className={`mystara-card p-2 flex flex-col items-center gap-1 ${activeView === 'visualizations' ? 'ring-2 ring-blue-400 bg-blue-50' : 'hover:bg-blue-50/50'}`}>
                <Eye className="w-4 h-4 text-blue-500" /><span className="text-[10px] text-purple-600">Visualize</span>
              </button>
              <button onClick={() => setActiveView('aligned-actions')} className={`mystara-card p-2 flex flex-col items-center gap-1 ${activeView === 'aligned-actions' ? 'ring-2 ring-amber-400 bg-amber-50' : 'hover:bg-amber-50/50'}`}>
                <Zap className="w-4 h-4 text-amber-500" /><span className="text-[10px] text-purple-600">Aligned Action</span>
              </button>
            </div>
          </div>

          <div className="mystara-glass p-4">
            <button onClick={() => setChatsExpanded(!chatsExpanded)} className="w-full flex items-center justify-between text-purple-700 mb-3">
              <span className="font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4" />Chats</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${chatsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {chatsExpanded && (
              <div>
                <input type="text" placeholder="Search..." value={chatSearchQuery} onChange={(e) => setChatSearchQuery(e.target.value)} className="mystara-input w-full mb-3" />
                <div className="space-y-2 max-h-32 overflow-y-auto mystara-scroll">
                  {conversationsLoading ? (
                    <p className="text-center text-sm text-purple-500 py-4">Loading...</p>
                  ) : filteredConversations.length === 0 ? (
                    <p className="text-center text-sm text-purple-500 py-4">{conversations.length === 0 ? "No conversations yet" : "No matches"}</p>
                  ) : (
                    filteredConversations.slice(0, 6).map((conv) => (
                      <button key={conv.id} onClick={() => handleLoadChat(conv.id)} className={`mystara-card w-full text-left p-2 group ${currentConversation?.id === conv.id ? 'ring-2 ring-purple-400' : ''}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-purple-700 truncate flex-1">{conv.title}</span>
                          <button onClick={(e) => handleDeleteChat(conv.id, e)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded">
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>

        <main className="mystara-main flex flex-col">
          <AnimatePresence mode="wait">
            {activeView === 'soul-file' && (
              <motion.div
                key="soul-file"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex-1"
              >
                <SoulFileDashboard embedded onClose={() => setActiveView('chat')} />
              </motion.div>
            )}

            {activeView === 'chat' && (
              <motion.div
                key="chat"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex-1 flex flex-col min-h-0"
              >
                <section className="mystara-glass flex-1 overflow-y-auto mystara-scroll p-6 mb-4 min-h-0">
                  {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-center"
                      >
                        <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                        <h3 className="text-2xl font-semibold mb-2 text-purple-700">
                          {soulFile?.essence.preferredName ? `Hey ${soulFile.essence.preferredName} ✨` : 'Welcome ✨'}
                        </h3>
                        <p className="text-purple-500 max-w-md mx-auto mb-6">What's on your mind?</p>
                        <div className="flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
                          {["I need clarity", "Help with a block", "Share a win", "Just checking in"].map((p) => (
                            <button
                              key={p}
                              onClick={() => setInputMessage(p)}
                              className="px-4 py-2 text-sm bg-white/50 hover:bg-white/70 text-purple-700 rounded-full transition-all border border-white/60 hover:border-purple-200"
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <AnimatePresence>
                        {messages.map((m) => (
                          <motion.div
                            key={m.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                          >
                            <div className={`px-5 py-3 max-w-[75%] rounded-3xl ${m.role === "user" ? "mystara-msg-user" : "mystara-msg-assistant"}`}>
                              <p className="text-[15px] leading-relaxed text-purple-700 whitespace-pre-wrap">{m.content}</p>
                              <p className="text-[11px] mt-2 text-purple-400">
                                {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>

                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="mystara-msg-assistant px-5 py-3 rounded-3xl">
                            <div className="flex gap-1.5">
                              <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" />
                              <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce [animation-delay:0.15s]" />
                              <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce [animation-delay:0.3s]" />
                            </div>
                          </div>
                        </div>
                      )}

                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </section>

                <footer className="mystara-glass p-4 flex-shrink-0">
                  <div className="flex gap-3">
                    <input type="text" value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type your message..." className="mystara-input flex-1" />
                    <button onClick={handleSendMessage} disabled={!inputMessage.trim() || isLoading} className="mystara-btn-send disabled:opacity-40">
                      <Send className="w-4 h-4" /><span className="hidden sm:inline">Send</span>
                    </button>
                  </div>
                </footer>
              </motion.div>
            )}

            {activeView === 'oracle' && (
              <motion.div key="oracle" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <OracleCardPull onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'affirmations' && (
              <motion.div key="affirmations" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <AffirmationGenerator onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'gratitude' && (
              <motion.div key="gratitude" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <GratitudeJournal onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'evidence' && (
              <motion.div key="evidence" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <EvidenceBank onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'aligned-actions' && (
              <motion.div key="aligned-actions" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <MicroChallenges onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'vision-board' && (
              <motion.div key="vision-board" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <VisionBoard onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'visualizations' && (
              <motion.div key="visualizations" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <GuidedVisualization onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'ritual-morning' && (
              <motion.div key="ritual-morning" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <DailyAlignment type="morning" onComplete={() => setActiveView('chat')} onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'ritual-evening' && (
              <motion.div key="ritual-evening" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <DailyAlignment type="evening" onComplete={() => setActiveView('chat')} onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}

            {activeView === 'ritual-reset' && (
              <motion.div key="ritual-reset" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1">
                <WeeklyRitual type="reset" onComplete={() => setActiveView('chat')} onClose={() => setActiveView('chat')} embedded />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default ChatPage;
export { ChatPage };
